
// Store
import { createVuexHelpers } from 'vue2-helpers'

// Event Bus
import { EventBus } from '@/utils/eventBus'

// Utils
import storage from '@/utils/storage.js'
const { getStorage } = storage

// Components
import Loader from '../components/Loaders/Loader.vue'
import {
	ref,
	onBeforeMount,
	defineComponent,
	watch,
	ComputedRef,
	onBeforeUnmount
} from 'vue'
import { useRouter, useRoute } from 'vue2-helpers/vue-router'

// Types
import {
	GetCreditCards,
	UserActions,
	VenueActions,
	VenueInfoState
} from '@/types/storeTypes'
import { WebsocketsData, WebsocketsEvent } from '@/types/entitiesTypes'
import {
	PAYMENT_FAILED_CODES_TICKETING,
	WebsocketEventTypes,
	WebsocketStringMessageEnum
} from '@/types/constantsTypes'
import {
	WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_CODE,
	WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_TEXT,
	WEB_SOCKET_STATE
} from '@/constants'
import { getWSUrl } from '@/utils/helpers'

export default defineComponent({
	components: { Loader },

	name: 'MainTicketing',

	setup() {
		let websocket = {} as WebSocket
		let isPingPongRunning = false
		// Store
		const { useActions, useState, useGetters } = createVuexHelpers()
		const { getVenueInfo }: VenueActions = useActions('venue', ['getVenueInfo'])
		const { login, getUser }: UserActions = useActions('user', [
			'login',
			'getUser'
		])
		const {
			getCreditCards
		}: {
			getCreditCards: GetCreditCards
		} = useActions('payment', ['getCreditCards', 'addCreditCard'])
		const { id: userId }: { id: ComputedRef<string> } = useState('user', ['id'])
		const { venueInfo }: VenueInfoState = useState('venue', ['venueInfo'])
		const {
			isWebVersionAllowSmoothTransition
		}: {
			isWebVersionAllowSmoothTransition: ComputedRef<boolean>
		} = useGetters('venue', ['isWebVersionAllowSmoothTransition'])

		// Router
		const router = useRouter()
		const route = useRoute()

		// Refs
		const loading = ref(false)
		const reconnectTimeout = ref(0)
		const pingTimeout = ref(0)

		// Watchers
		watch(userId, (nv, ov) => {
			if (nv && nv !== ov) {
				listenWebsockets()
			}
		})

		// Hooks
		onBeforeMount(async () => {
			const {
				params: { subDomain, venueChainId },
				name: routeName
			} = route

			loading.value = true

			try {
				await getVenueInfo({
					venueChainSubdomain: subDomain,
					venueId: venueChainId
				})

				if (
					!venueInfo.value.ticketingSettings.isTicketingEnabled &&
					!venueInfo.value.ticketingSettings.hasActiveEvents
				) {
					if (venueInfo.value.onlineMenuUrl) {
						window.location.href = `${venueInfo.value.onlineMenuUrl}?isTicketingUnavailable=true`
					} else {
						storage.removeFromStorage('lastVenueChainSubdomain')
						storage.removeFromStorage('lastVenueId')
						await router.push({ name: 'notFound' })

						return
					}
				}

				if (!getStorage('token')) {
					await login()
				}

				await Promise.all([getUser(venueInfo.value.id), getCreditCards()])

				if (routeName === 'main') {
					await router.push({ name: 'events' })
				}
			} catch (e) {
				await router.push({ name: 'notFound' })
			} finally {
				loading.value = false
			}
		})

		onBeforeUnmount(() => {
			reconnectTimeout.value && clearTimeout(reconnectTimeout.value)
			pingTimeout.value && clearTimeout(pingTimeout.value)
		})

		// TODO: Add websockets composable(4.0)
		const listenWebsockets = (isReconnect = false): void => {
			if (!userId.value) return
			// Means that ws has already connected and need to close prev connection
			if (
				websocket &&
				(websocket.readyState === WEB_SOCKET_STATE.connecting ||
					websocket.readyState === WEB_SOCKET_STATE.open)
			) {
				websocket.close(
					WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_CODE,
					WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_TEXT
				)
			}

			const wsUri = `${getWSUrl()}/pubsub/account/${userId.value}`
			websocket = new WebSocket(wsUri)

			if (isReconnect) {
				// TODO: implement smt on reconnect
			}

			websocket.onclose = info => {
				isPingPongRunning = false

				if (
					info &&
					info.code === WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_CODE &&
					info.reason === WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_TEXT
				) {
					return
				}

				reconnectTimeout.value = setTimeout(() => {
					listenWebsockets(true)
				}, 3000)
			}

			websocket.onopen = () => {
				isPingPongRunning = true
				websocket.send(WebsocketStringMessageEnum.ping)
			}

			websocket.onmessage = (e: WebsocketsEvent): void => {
				if (e.data === WebsocketStringMessageEnum.pong) {
					if (isPingPongRunning) {
						pingTimeout.value = setTimeout(() => {
							websocket.send(WebsocketStringMessageEnum.ping)
						}, 5000)
					}

					return
				}

				const data = JSON.parse(e.data) as WebsocketsData

				if (data.venueId !== venueInfo.value.id) return

				switch (WebsocketEventTypes[data.type]) {
					case 'TICKET_ORDER_PAID_FAILED':
						if (isWebVersionAllowSmoothTransition.value) {
							if (
								PAYMENT_FAILED_CODES_TICKETING.includes(Number(data.errorCode))
							) {
								EventBus.$emit('ticket-order-paid-failed')
							} else {
								EventBus.$emit('ticketing-service-error')
							}
						} else {
							EventBus.$emit('ticket-order-paid-failed')
						}
						break

					case 'TICKET_ORDER_PARTIAL_PAYMENT':
						EventBus.$emit('ticket-order-partial-payment')
						break

					case 'TICKET_ORDER_PAID_SUCCESS':
						if (!isWebVersionAllowSmoothTransition.value) {
							EventBus.$emit('update-purchase-order')
						}
						break

					default:
						break
				}
			}

			websocket.onerror = () => {
				console.log('-------WEBSOCKET ERROR---')
			}
		}

		return {
			loading
		}
	}
})
